import type {DataRouterFileOverviewPayload, OverviewPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RecentlyTouchedBranches} from './RecentlyTouchedBranches'
import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {InterractionLimitsBanner} from './banners/InterractionLimitsBanner'
import PendingInvitationBanner from './banners/PendingInvitationBanner'
import {ProtectBranchBanner} from './banners/ProtectBranchBanner'
import {UseActionBanner} from './banners/UseActionBanner'
import {ImmutableActionsMigrationBanner} from './banners/ImmutableActionsMigrationBanner'
import {DeployBanner} from './banners/DeployBanner'
import {SpammyBanner} from './banners/SpammyBanner'
import {BlockedContributors} from './banners/BlockedContributors'
import {TransactionalMessageBanner} from '@github-ui/in-product-targeting'
import PublishBanners from '@github-ui/code-view-shared/components/PublishBanners'
import {SpoofedCommitWarningBanner} from '@github-ui/code-view-shared/components/SpoofedCommitWarning'
import {CopilotSurvey} from '@github-ui/copilot-survey-card'

import styles from './OverviewHeader.module.css'
import {useCurrentUser} from '@github-ui/current-user'
import {useLatestCommitContext} from '@github-ui/code-view-shared/contexts/LatestCommitContext'

export function OverviewHeader({payload}: {payload: DataRouterFileOverviewPayload}) {
  const repo = useCurrentRepository()
  const user = useCurrentUser()

  return (
    <div className={styles.Box}>
      {user && <RecentlyTouchedBranches repo={repo} />}
      <OverviewBanners overview={payload.overview} />
    </div>
  )
}

function OverviewBanners({overview}: {overview: OverviewPayload}) {
  const {latestCommit} = useLatestCommitContext()
  const isSpoofed = latestCommit?.isSpoofed ?? false
  const {
    showUseActionBanner,
    showProtectBranchBanner,
    actionId,
    actionSlug,
    copilotSurveyBanner,
    publishBannersInfo: {dismissActionNoticePath, releasePath, showPublishActionBanner},
    interactionLimitBanner,
    showInvitationBanner,
    inviterName,
    actionsMigrationBannerInfo,
    showDeployBanner,
    detectedStack,
    transactionalMessageBanner,
    showSpammyBanner,
    blockedContributorsBanner,
  } = overview.banners
  const {helpUrl} = useReposAppPayload()

  return (
    <>
      {isFeatureEnabled('ipm_global_transactional_message_repos') && transactionalMessageBanner ? (
        <TransactionalMessageBanner banner={transactionalMessageBanner} />
      ) : null}
      {showDeployBanner && <DeployBanner detectedStack={detectedStack} />}
      {isSpoofed && <SpoofedCommitWarningBanner />}
      {interactionLimitBanner && <InterractionLimitsBanner interactionLimitBanner={interactionLimitBanner} />}
      {showInvitationBanner && inviterName && <PendingInvitationBanner inviterName={inviterName} />}
      {showSpammyBanner && <SpammyBanner />}
      {blockedContributorsBanner && <BlockedContributors banner={blockedContributorsBanner} />}
      <PublishBanners
        showPublishActionBanner={showPublishActionBanner}
        releasePath={releasePath}
        dismissActionNoticePath={dismissActionNoticePath}
        className={styles.PublishBanners}
      />
      {showUseActionBanner && actionSlug && actionId ? (
        <UseActionBanner actionSlug={actionSlug} actionId={actionId} />
      ) : null}
      {copilotSurveyBanner && <CopilotSurvey {...copilotSurveyBanner} />}
      {showProtectBranchBanner && <ProtectBranchBanner helpUrl={helpUrl} />}
      {actionsMigrationBannerInfo.showImmutableActionsMigrationBanner && (
        <ImmutableActionsMigrationBanner
          releaseTags={actionsMigrationBannerInfo.releaseTags}
          initialMigrationStatus={actionsMigrationBannerInfo.initialMigrationStatus}
        />
      )}
    </>
  )
}
